import {
    AreaNotification,
    ErrorIcon,
    mergeClasses,
    NoResultsText,
    Text,
    Tile,
    TilesSection,
    useAsyncEffect,
    useRouter,
    useStatsTileStyles,
    useTileStyles,
} from "@relode/core";
import { JobCandidateStatus } from "@relode/core/domain";
import qs from "qs";
import * as React from "react";

import { useJobContext } from "../../../_infra";

export const JobCandidates = () => {
    const classes = mergeClasses(useStatsTileStyles({}), useTileStyles({}));
    const { history } = useRouter();

    const statusTile = {
        root: classes.statusTile,
        title: classes.statusTitle,
        content: classes.statusContent,
        paper: classes.tilePaper,
    };

    const {
        state: {
            ui: {
                isJobLoading,
                isJobCandidatesFunnelLoading,
                jobCandidatesFunnelError,
            },
            data: { currentJobId, job },
        },
        actions,
    } = useJobContext();

    const jobDetails = job?.details;
    const jobCandidatesFunnel = job?.jobCandidatesFunnel;

    useAsyncEffect(async () => {
        await actions.loadJobCandidatesFunnel(currentJobId);
    }, [currentJobId]);

    const goToCandidates = (candidateStatuses: JobCandidateStatus[]) => {
        const ids = candidateStatuses.map(x => x.id);
        const names = candidateStatuses.map(x => x.description);

        const criteria = {
            jobCandidateStatusIds: ids,
            jobCandidateStatusNames: names,
            jobId: currentJobId,
            jobName: jobDetails?.jobName,
            pageIndex: 1,
            pageSize: 25,
        };
        history.push(
            `/candidates${qs.stringify(criteria, {
                skipNulls: true,
                addQueryPrefix: true,
                sort: (a, b) => a.localeCompare(b),
            })}`
        );
    };

    const getSubTitleText = (count: number | undefined, label: string) => (
        <>
            <Text variant="h5" isInline={true}>
                {count ?? 0}
            </Text>{" "}
            {label}
        </>
    );

    const isBusy = isJobLoading || isJobCandidatesFunnelLoading;

    const displayStat = (value: number | undefined | null) => {
        if (isBusy || value === undefined || value === null) return "n/a";
        return value;
    };

    const getTitleAdornment = (titleAdornment: React.ReactNode) =>
        !!jobCandidatesFunnelError ? <ErrorIcon /> : titleAdornment;

    const getErrorTooltip = () =>
        !!jobCandidatesFunnelError
            ? jobCandidatesFunnelError.friendlyMessages ??
              "An unexpected error occurred.  Please try again."
            : null;

    return (
        <>
            <AreaNotification areaName="teamPlanWarning" />
            <div className={classes.firstSectionWrapper}>
                <div>
                    <Text variant="h2">candidates</Text>
                </div>
                <div>
                    {jobDetails?.jobStatus.isPendingOrRequested() ? (
                        <NoResultsText
                            data-cy="noResultsText"
                            className={classes.notAvailableText}
                        >
                            Not available for jobs in Pending or Requested
                            status.
                        </NoResultsText>
                    ) : (
                        <TilesSection
                            usePaper={false}
                            classes={{ paper: classes.tilesSectionPaper }}
                            className={classes.tilesSection}
                        >
                            <Tile
                                small
                                useSubTitle
                                id="newAndQualifyingCount"
                                classes={statusTile}
                                title="new/qualifying"
                                isBusy={isBusy}
                                titleAdornment={getTitleAdornment(null)}
                                tooltip={getErrorTooltip()}
                            >
                                {displayStat(
                                    jobCandidatesFunnel?.newAndQualifyingCandidateCount
                                )}
                            </Tile>
                            <Tile
                                small
                                useSubTitle
                                id="reviewCount"
                                classes={statusTile}
                                title="review"
                                isBusy={isBusy}
                                titleAdornment={getTitleAdornment(null)}
                                tooltip={getErrorTooltip()}
                            >
                                {displayStat(
                                    jobCandidatesFunnel?.reviewCandidateCount
                                )}
                            </Tile>
                            <Tile
                                small
                                useSubTitle
                                id="submittedCount"
                                classes={statusTile}
                                title="submitted"
                                isBusy={isBusy}
                                showAsLink={
                                    jobCandidatesFunnel?.submittedCandidateCount >
                                    0
                                }
                                titleAdornment={getTitleAdornment(
                                    jobCandidatesFunnel?.submittedCandidateCount >
                                        0 ? (
                                        <span
                                            className={classes.badgeDot}
                                        ></span>
                                    ) : null
                                )}
                                tooltip={getErrorTooltip()}
                                subTitle={getSubTitleText(
                                    jobCandidatesFunnel?.uniqueSubmittedCandidateCount,
                                    "total"
                                )}
                                onClick={() =>
                                    goToCandidates([
                                        JobCandidateStatus.Submitted,
                                    ])
                                }
                            >
                                {displayStat(
                                    jobCandidatesFunnel?.submittedCandidateCount
                                )}
                            </Tile>
                            <Tile
                                small
                                useSubTitle
                                id="interviewingCount"
                                classes={statusTile}
                                title="interviewing"
                                isBusy={isBusy}
                                showAsLink={
                                    jobCandidatesFunnel?.interviewingCandidateCount >
                                    0
                                }
                                titleAdornment={getTitleAdornment(
                                    jobCandidatesFunnel?.interviewingCandidateCount >
                                        0 ? (
                                        <span
                                            className={classes.badgeDot}
                                        ></span>
                                    ) : null
                                )}
                                tooltip={getErrorTooltip()}
                                onClick={() =>
                                    goToCandidates([
                                        JobCandidateStatus.Interviewing,
                                    ])
                                }
                            >
                                {displayStat(
                                    jobCandidatesFunnel?.interviewingCandidateCount
                                )}
                            </Tile>
                            <Tile
                                small
                                useSubTitle
                                id="offeredCount"
                                classes={statusTile}
                                title="offered"
                                isBusy={isBusy}
                                showAsLink={
                                    jobCandidatesFunnel?.offeredCandidateCount >
                                    0
                                }
                                titleAdornment={getTitleAdornment(
                                    jobCandidatesFunnel?.offeredCandidateCount >
                                        0 ? (
                                        <span
                                            className={classes.badgeDot}
                                        ></span>
                                    ) : null
                                )}
                                tooltip={getErrorTooltip()}
                                onClick={() =>
                                    goToCandidates([JobCandidateStatus.Offered])
                                }
                            >
                                {displayStat(
                                    jobCandidatesFunnel?.offeredCandidateCount
                                )}
                            </Tile>
                            <Tile
                                small
                                useSubTitle
                                id="hiredCount"
                                classes={statusTile}
                                title="hired"
                                isBusy={isBusy}
                                showAsLink={
                                    jobCandidatesFunnel?.hiredCandidateCount > 0
                                }
                                titleAdornment={getTitleAdornment(
                                    jobCandidatesFunnel?.hiredCandidateCount >
                                        0 ? (
                                        <span
                                            className={classes.badgeDot}
                                        ></span>
                                    ) : null
                                )}
                                tooltip={getErrorTooltip()}
                                onClick={() =>
                                    goToCandidates([JobCandidateStatus.Hired])
                                }
                            >
                                {displayStat(
                                    jobCandidatesFunnel?.hiredCandidateCount
                                )}
                            </Tile>
                            <Tile
                                small
                                useSubTitle
                                id="rejectedCount"
                                classes={statusTile}
                                title="rejected (sub+)"
                                isBusy={isBusy}
                                showAsLink={
                                    jobCandidatesFunnel?.uniqueRejectedFromSubmittedPlusCandidateCount >
                                    0
                                }
                                titleAdornment={getTitleAdornment(null)}
                                tooltip={getErrorTooltip()}
                                subTitle={getSubTitleText(
                                    jobCandidatesFunnel?.rejectedCandidateCount,
                                    "total"
                                )}
                                onClick={() =>
                                    goToCandidates([
                                        JobCandidateStatus.Rejected,
                                    ])
                                }
                            >
                                {displayStat(
                                    jobCandidatesFunnel?.uniqueRejectedFromSubmittedPlusCandidateCount
                                )}
                            </Tile>
                        </TilesSection>
                    )}
                </div>
            </div>
        </>
    );
};
